import React, { useState, useMemo } from 'react';
import {
  Container, Row, Col,
} from 'react-bootstrap';
import { SortDirection } from 'constants/sorting';
import Button from 'components/DesignSystem/Button';
import ColumnHeaderSelectSort from '.';
import 'components/NewHome/Table/index.scss';

// Mock data for demonstration
const regionOptions = [
  {
    label: 'Norte',
    value: 'north',
  },
  {
    label: 'Nordeste',
    value: 'northeast',
  },
  {
    label: 'Centro-Oeste',
    value: 'midwest',
  },
  {
    label: 'Sudeste',
    value: 'southeast',
  },
  {
    label: 'Sul',
    value: 'south',
  },
];

const variableOptions = [
  {
    label: 'Temperatura',
    value: 'temp',
  },
  {
    label: 'Precipitação',
    value: 'precipitation',
  },
  {
    label: 'Umidade',
    value: 'humidity',
  },
  {
    label: 'Vento',
    value: 'wind',
  },
  {
    label: 'Pressão Atmosférica',
    value: 'pressure',
  },
];

const monthOptions = [
  {
    label: 'Janeiro',
    value: 'jan',
  },
  {
    label: 'Fevereiro',
    value: 'feb',
  },
  {
    label: 'Março',
    value: 'mar',
  },
  {
    label: 'Abril',
    value: 'apr',
  },
  {
    label: 'Maio',
    value: 'may',
  },
  {
    label: 'Junho',
    value: 'jun',
  },
  {
    label: 'Julho',
    value: 'jul',
  },
  {
    label: 'Agosto',
    value: 'aug',
  },
  {
    label: 'Setembro',
    value: 'sep',
  },
  {
    label: 'Outubro',
    value: 'oct',
  },
  {
    label: 'Novembro',
    value: 'nov',
  },
  {
    label: 'Dezembro',
    value: 'dec',
  },
];

// Mock table data - Dados mais extensos para demonstração
const mockTableData = [
  {
    id: 1, region: 'Norte', variable: 'Temperatura', month: 'Janeiro', value: 28.5, unit: '°C',
  },
  {
    id: 2, region: 'Sudeste', variable: 'Precipitação', month: 'Janeiro', value: 150, unit: 'mm',
  },
  {
    id: 3, region: 'Sul', variable: 'Temperatura', month: 'Janeiro', value: 22.1, unit: '°C',
  },
  {
    id: 4, region: 'Nordeste', variable: 'Umidade', month: 'Janeiro', value: 75, unit: '%',
  },
  {
    id: 5, region: 'Centro-Oeste', variable: 'Vento', month: 'Janeiro', value: 12.3, unit: 'km/h',
  },
  {
    id: 6, region: 'Norte', variable: 'Precipitação', month: 'Fevereiro', value: 320, unit: 'mm',
  },
  {
    id: 7, region: 'Sudeste', variable: 'Temperatura', month: 'Fevereiro', value: 25.8, unit: '°C',
  },
  {
    id: 8, region: 'Sul', variable: 'Umidade', month: 'Fevereiro', value: 68, unit: '%',
  },
  {
    id: 9, region: 'Nordeste', variable: 'Vento', month: 'Fevereiro', value: 15.7, unit: 'km/h',
  },
  {
    id: 10, region: 'Centro-Oeste', variable: 'Temperatura', month: 'Fevereiro', value: 31.2, unit: '°C',
  },
  {
    id: 11, region: 'Norte', variable: 'Umidade', month: 'Março', value: 82, unit: '%',
  },
  {
    id: 12, region: 'Sudeste', variable: 'Vento', month: 'Março', value: 8.9, unit: 'km/h',
  },
  {
    id: 13, region: 'Sul', variable: 'Precipitação', month: 'Março', value: 95, unit: 'mm',
  },
  {
    id: 14, region: 'Nordeste', variable: 'Temperatura', month: 'Março', value: 29.4, unit: '°C',
  },
  {
    id: 15, region: 'Centro-Oeste', variable: 'Precipitação', month: 'Março', value: 180, unit: 'mm',
  },
];

/**
 * Componente de demonstração do ColumnHeaderSelectSort
 * Mostra como usar o componente em tabelas com Row/Col (padrão FluxQueue)
 */
export default function ColumnHeaderSelectSortDemo() {
  // Estados para os filtros
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedVariables, setSelectedVariables] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>(''); // Single select para Mês

  // Estados para ordenação
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>();

  // Mapeamento de values para labels
  const regionValueToLabel = useMemo(() => {
    const map: { [key: string]: string } = {};
    regionOptions.forEach((option) => {
      map[option.value] = option.label;
    });
    return map;
  }, []);

  const variableValueToLabel = useMemo(() => {
    const map: { [key: string]: string } = {};
    variableOptions.forEach((option) => {
      map[option.value] = option.label;
    });
    return map;
  }, []);

  const monthValueToLabel = useMemo(() => {
    const map: { [key: string]: string } = {};
    monthOptions.forEach((option) => {
      map[option.value] = option.label;
    });
    return map;
  }, []);

  // Handler para ordenação - mantém apenas uma coluna ordenada por vez
  const handleSort = (field: string) => (direction?: SortDirection) => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Filtrar e ordenar dados da tabela
  const processedData = useMemo(() => {
    // Primeiro filtrar
    let filtered = mockTableData.filter((item) => {
      // Mapear os values selecionados para labels para comparar com os dados
      const selectedRegionLabels = selectedRegions.map((value) => regionValueToLabel[value]);
      const selectedVariableLabels = selectedVariables.map((value) => variableValueToLabel[value]);
      const selectedMonthLabel = selectedMonth ? monthValueToLabel[selectedMonth] : '';

      const regionMatch = selectedRegions.length === 0
        || selectedRegionLabels.includes(item.region);
      const variableMatch = selectedVariables.length === 0
        || selectedVariableLabels.includes(item.variable);
      const monthMatch = !selectedMonth
        || selectedMonthLabel === item.month;

      return regionMatch && variableMatch && monthMatch;
    });

    // Depois ordenar se houver configuração de ordenação
    if (sortField && sortDirection) {
      filtered = [...filtered].sort((a, b) => {
        let comparison = 0;

        if (sortField === 'Região') {
          comparison = a.region.localeCompare(b.region);
        } else if (sortField === 'Variável') {
          comparison = a.variable.localeCompare(b.variable);
        } else if (sortField === 'Mês') {
          comparison = a.month.localeCompare(b.month);
        }

        return sortDirection === 'ASC' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [
    selectedRegions,
    selectedVariables,
    selectedMonth,
    sortField,
    sortDirection,
    regionValueToLabel,
    variableValueToLabel,
    monthValueToLabel,
  ]);

  const clearFilters = () => {
    setSelectedRegions([]);
    setSelectedVariables([]);
    setSelectedMonth('');
    setSortField(undefined);
    setSortDirection(undefined);
  };

  return (
    <Container className="py-4">
      <h1>Demo: ColumnHeaderSelectSort Component</h1>
      <p>
        Demonstração do componente ColumnHeaderSelectSort usando Row/Col (padrão do sistema).
        Filtro à esquerda, ordenação à direita.
      </p>

      <hr />

      {/* Botão de Limpar Filtros */}
      <Row className="d-flex justify-content-end">
        <Col md="2" className="d-flex justify-content-end me-3">
          <Button
            type="tertiary"
            icon="no_filter"
            onClick={clearFilters}
          >
            Limpar Filtros
          </Button>
        </Col>
      </Row>

      {/* Cabeçalho da Tabela */}
      <div className="ms-2 me-2">
        <Row className="mb-1 mt-2 ht-row">
          <Col md={1} className="mt-1 d-flex align-items-center justify-content-start">
            <strong>ID</strong>
          </Col>
          <ColumnHeaderSelectSort
            md={3}
            title="Região"
            value={selectedRegions}
            setValue={setSelectedRegions}
            onSort={handleSort('Região')}
            sortDirection={sortField === 'Região' ? sortDirection : undefined}
          >
            {regionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </ColumnHeaderSelectSort>
          <ColumnHeaderSelectSort
            md={3}
            title="Variável"
            value={selectedVariables}
            setValue={setSelectedVariables}
            onSort={handleSort('Variável')}
            sortDirection={sortField === 'Variável' ? sortDirection : undefined}
          >
            {variableOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </ColumnHeaderSelectSort>
          <ColumnHeaderSelectSort
            md={3}
            title="Mês"
            value={selectedMonth}
            setValue={setSelectedMonth}
            onSort={handleSort('Mês')}
            sortDirection={sortField === 'Mês' ? sortDirection : undefined}
          >
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </ColumnHeaderSelectSort>
          <Col md={2} className="mt-1 d-flex align-items-center justify-content-start">
            <strong>Valor</strong>
          </Col>
        </Row>

        {/* Linhas de Dados */}
        <div className="ht-row-container">
          {processedData.length > 0 ? (
            processedData.map((item) => (
              <Row key={`data-${item.id}`} className="pt-3 pb-3 ht-row ht-entry-row">
                <Col md={1} className="home-table-entry-attr">{item.id}</Col>
                <Col md={3} className="home-table-entry-name">{item.region}</Col>
                <Col md={3} className="home-table-entry-attr">{item.variable}</Col>
                <Col md={3} className="home-table-entry-attr">{item.month}</Col>
                <Col md={2} className="home-table-entry-attr">
                  {item.value}
                  {item.unit}
                </Col>
              </Row>
            ))
          ) : (
            <Row className="pt-3 pb-3 ht-row">
              <Col className="text-center text-muted">
                Nenhum dado encontrado com os filtros selecionados
              </Col>
            </Row>
          )}
        </div>
      </div>

      <hr className="my-5" />

      <div className="alert alert-info">
        <h4>Como Usar ColumnHeaderSelectSort:</h4>
        <ol>
          <li>
            <strong>Ordenação (esquerda):</strong>
            {' '}
            Clique nas setas para ordenar (ASC/DESC/Sem ordenação)
          </li>
          <li>
            <strong>Label (centro):</strong>
            {' '}
            Mostra o título da coluna
          </li>
          <li>
            <strong>Filtro (direita):</strong>
            {' '}
            Clique no ícone de filtro para selecionar opções
          </li>
          <li>
            <strong>Badge:</strong>
            {' '}
            Mostra quantos filtros estão ativos na coluna
          </li>
          <li>
            <strong>Multi-seleção vs Seleção única:</strong>
            {' '}
            O componente detecta automaticamente o tipo baseado no value
            (array = multi, string/number = single)
          </li>
          <li>
            <strong>Comportamento:</strong>
            {' '}
            Filtros e ordenação funcionam juntos - você pode filtrar e ordenar simultaneamente
          </li>
        </ol>

        <h6>Exemplo de uso no código (Multi-seleção):</h6>
        <pre className="bg-light p-2 rounded mt-3" style={{ fontSize: '0.8rem' }}>
          {`<ColumnHeaderSelectSort
  md={3}
  title="Região"
  value={selectedRegions}  // string[]
  setValue={setSelectedRegions}
  onSort={handleSort('Região')}
  sortDirection={sortField === 'Região' ? sortDirection : undefined}
>
  {regionOptions.map((option) => (
    <option key={option.value} value={option.value}>
      {option.label}
    </option>
  ))}
</ColumnHeaderSelectSort>`}
        </pre>

        <h6>Exemplo de uso no código (Seleção única):</h6>
        <pre className="bg-light p-2 rounded mt-3" style={{ fontSize: '0.8rem' }}>
          {`<ColumnHeaderSelectSort
  md={3}
  title="Mês"
  value={selectedMonth}  // string
  setValue={setSelectedMonth}
  onSort={handleSort('Mês')}
  sortDirection={sortField === 'Mês' ? sortDirection : undefined}
>
  {monthOptions.map((option) => (
    <option key={option.value} value={option.value}>
      {option.label}
    </option>
  ))}
</ColumnHeaderSelectSort>`}
        </pre>
      </div>

      <div className="alert alert-success mt-3">
        <h5>Informações do Estado Atual:</h5>
        <ul className="mb-0">
          <li>
            <strong>Regiões Selecionadas (Multi-seleção):</strong>
            {' '}
            {selectedRegions.length > 0 ? selectedRegions.map((v) => regionValueToLabel[v]).join(', ') : 'Nenhuma'}
          </li>
          <li>
            <strong>Variáveis Selecionadas (Multi-seleção):</strong>
            {' '}
            {selectedVariables.length > 0 ? selectedVariables.map((v) => variableValueToLabel[v]).join(', ') : 'Nenhuma'}
          </li>
          <li>
            <strong>Mês Selecionado (Seleção única):</strong>
            {' '}
            {selectedMonth ? monthValueToLabel[selectedMonth] : 'Nenhum'}
          </li>
          <li>
            <strong>Ordenação:</strong>
            {' '}
            {sortField && sortDirection ? `${sortField} (${sortDirection})` : 'Nenhuma'}
          </li>
          <li>
            <strong>Total de Registros:</strong>
            {' '}
            {processedData.length}
            {' '}
            de
            {' '}
            {mockTableData.length}
          </li>
        </ul>
      </div>
    </Container>
  );
}
